uniform float uTime;
uniform float uDeltaTime;
uniform vec3 uMouse;
uniform float uMouseRadius;
uniform float uMouseAspect;
uniform float uSuckAmount;
uniform float uSuckStrength;
uniform vec3 uMouseVelocity;
uniform float uWindStrength;
uniform float uDamping;
uniform float uTurbulenceStrength;
uniform float uTurbulenceFrequency;
uniform float uFlowFieldFrequency;
uniform float uFlowFieldStrength;
uniform float uFlowFieldInfluence;
uniform sampler2D uBase;
uniform sampler2D uBase2;
uniform float uMorphAmount;
uniform float uMorphStrength;

#include ../includes/simplexNoise4d.glsl

// Rectangular distance: returns 0..1 normalized distance inside the box
float rectDist(vec3 pos, vec3 center, float radius, float aspect)
{
    vec3 d = abs(pos - center);
    float dx = d.x / (radius * aspect);
    float dy = d.y / (radius / aspect);
    float dz = d.z / radius;
    return max(max(dx, dy), dz);
}

void main()
{
    float time = uTime * 0.2;
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec4 velocity = texture(uVelocity, uv);
    vec4 particle = texture(uParticles, uv);

    // Reset velocity when particle is dead
    if(particle.a >= 1.0)
    {
        gl_FragColor = vec4(0.0);
        return;
    }

    vec4 base = texture(uBase, uv);

    // Flow field force (2 noise calls — combined axes from single noise derivatives)
    if(uFlowFieldStrength > 0.001)
    {
        float influence = (uFlowFieldInfluence - 0.5) * (-2.0);
        float n1 = simplexNoise4d(vec4(particle.xyz * uFlowFieldFrequency, time));
        float n2 = simplexNoise4d(vec4(particle.xyz * uFlowFieldFrequency + 7.0, time));
        vec3 flowField = normalize(vec3(n1, n2, n1 * n2));
        float strength = smoothstep(influence, 1.0, n1);
        velocity.xyz += flowField * strength * uFlowFieldStrength * uDeltaTime;
    }

    // Mouse wind force — localized around cursor
    vec3 mouseVel = uMouseVelocity;
    float mouseSpeedSq = dot(mouseVel, mouseVel);
    if(uSuckAmount < 0.1 && mouseSpeedSq > 0.01)
    {
        float windRadius = uMouseRadius * 0.3;
        vec3 diff = particle.xyz - uMouse;
        float distSq = dot(diff, diff);
        float windRadiusSq = windRadius * windRadius;
        if(distSq < windRadiusSq)
        {
            float dist = sqrt(distSq);
            float proximity = smoothstep(windRadius, 0.0, dist);
            velocity.xyz += mouseVel * proximity * uWindStrength * uDeltaTime;
        }
    }

    // Suck toward mouse (when clicking)
    if(uSuckAmount > 0.01)
    {
        float rd = rectDist(particle.xyz, uMouse, uMouseRadius, uMouseAspect);
        if(rd < 1.0)
        {
            vec3 toMouse = uMouse - particle.xyz;
            vec3 suckDir = normalize(toMouse);
            float proximity = smoothstep(1.0, 0.0, rd);
            velocity.xyz += suckDir * proximity * uSuckAmount * uSuckStrength * uDeltaTime;
        }
    }

    // Morph attraction — pull particles toward current morph target
    if(uMorphStrength > 0.0)
    {
        vec3 base1 = texture(uBase, uv).xyz;
        vec3 base2 = texture(uBase2, uv).xyz;
        vec3 morphTarget = mix(base1, base2, uMorphAmount);
        vec3 toTarget = morphTarget - particle.xyz;
        velocity.xyz += toTarget * uMorphStrength * uDeltaTime;
    }

    // Turbulence (2 noise calls — derive third axis)
    if(uTurbulenceStrength > 0.001)
    {
        float t1 = simplexNoise4d(vec4(particle.xyz * uTurbulenceFrequency, time * 3.0 + 10.0));
        float t2 = simplexNoise4d(vec4(particle.xyz * uTurbulenceFrequency, time * 3.0 + 20.0));
        vec3 turbulence = vec3(t1, t2, t1 - t2);
        velocity.xyz += turbulence * uTurbulenceStrength * uDeltaTime;
    }

    // Damping
    velocity.xyz *= (1.0 - uDamping * uDeltaTime);

    gl_FragColor = velocity;
}
